import React, {Fragment, useRef, useEffect, useState} from 'react'; 
import { observer } from 'mobx-react'; 
import { CollapseMenu } from 'cd-eft/components/common/CollapseMenu'; 

export const PopupContainer = (props: { title?: String }) => {
    if (props.title === undefined) {
        return (
            <div className= 'waterway-design-details'>
                <CollapseMenu
                titleClass= {'waterway-design-details-menu'} 
                noIcon= {true} 
                title= {'Profile'}
                isOpen= {true} 
                >
                    <ProfileDetails className= "pb-0 pl-3 pr-3" />
                </CollapseMenu>
                <CollapseMenu
                titleClass= {'waterway-design-details-menu'} 
                noIcon= {true} 
                title= {'Waterway Reaches'} 
                isOpen= {true} 
                >
                    <WaterWayReachesDetails className= "pb-0 pt-0 pl-3 pr-3" />
                </CollapseMenu>
            </div>
        );
    }
    return (
        <div>
            {props.title === 'Profile' && <ProfileDetails />} 
            {props.title === 'Waterway Profile Reaches' && <WaterWayReachesDetails />} 
        </div>
    ); 
}; 

export const WaterWayDesignDetails = observer(PopupContainer);

export const ProfileDetails = (props: { className?: string }) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                The Profile page initially displays the ground surface elevation along the selected alignment, the left bank, the right bank, 
                or merged low bank elevations. Select left/right bank display or merged low band display using the <strong>Settings</strong> button and enter a 
                value in the Low Bank Offset field.
            </p>
            <p>
                Vertical dashed red lines are also displayed showing the outer limits for reach digitization. The vertical red lines are located 
                at the beginning and ending stations of the profile or are adjusted inward by the minimum required distances for Channel Ends if 
                any are entered by length in the Waterway Inputs page. If Channel Ends are initially entered as a slope percentage, distances 
                cannot be calculated prior to the reaches being digitized. 
            </p>
            <p>
                The profile Legend defaults on but can be hidden using the Legend button on the top right side of the display. 
                <strong>The Show Coordinates </strong> button is the second button from the top on the right side of the display and 
                will toggle on and off a floating coordinate display for the cursor location on the Profile page. 
            </p>
            <p>
                <strong>The Settings </strong> button additionally allows an option to Show Fine Grid and a selection for the horizontal 
                interval for the finer grid display. The Sample Interval controls the distance between points along the alignment the application uses to generate 
                the ground surface profile.
            </p>
            <p>
                The Digitize Reach button is used to create the waterway channel profile and select breaks between reaches to establish 
                the channel grade(s). The <strong>Insert Station, Move Station,</strong> and <strong>Delete Station</strong> buttons all allow 
                the digitized reaches to be modified. 
                <ol>
                    <li>Selecting either Insert Station or Delete Station highlights the existing reach stations in orange to indicate 
                    where a new station will be added or an existing station will be removed. </li> 

                    <li>Selecting Move Station highlights the existing stations in orange and shows a white circle at the center point of each 
                        existing reach to help visualize changes. Each modification is a pending edit that is not incorporated into the Reach Table 
                        until it is Saved. </li>
                </ol>
            </p>
        </div>
    ); 
}; 

export const WaterWayReachesDetails = (props: { className?: string }) => {
    return (
        <div className={props.className ? props.className : ''} tabIndex={0}>
            <p>
                The <strong>Waterway Profile/Reaches Table</strong> has 3 different display options.
                <ol>
                    <li>The Station View displays values for each vertex digitized along profile. </li>

                    <li>The Reach View displays channel properties between vertices. </li>

                    <li>The Combined View shows all the information from both the Reach and Station View tables.</li>

                    <li>The display defaults to the Combined View. Edits made in the Reach Table are reflected in the 
                        Profile View when Saved.</li>
                </ol>
            </p>
            <p>
                If changes are needed to noneditable values, utilize the Action icon on the right side of the display. 
                The available action items vary depending which view is currently being used.
            </p>
            <p>
                The action item <strong>S/V/C-WSC</strong> stands for Soil Strength (S), Vegetal Cover (V), and Channel Properties (C) 
                in the Waterway Sizing Calculator (WSC).
                <ol type= 'a'>
                    <li>The waterway properties affecting tractive stress calculations may be changed for a single reach or multiple 
                        selected reaches utilizing the check boxes on the left side of the Reaches Table. </li>

                    <li>The calculator allows for options to be evaluated by utilizing the Calculate button. </li>

                    <li>Changes are not incorporated into the design until the Apply Properties to Selected Reaches button is used. 
                        Additional information is available in the Waterway Sizing Calculator.</li>
                </ol>
            </p>
            <p>
            The Save button allow edits to be made and saved. The Save &amp; Calculate button saves all edits and performs waterway 
            calculations for all reaches. Once the calculations have been completed the Generate Surface button becomes active. 
            The Generate Surface button incorporates the designed waterway into the original ground surface, creating a new designed surface. 
            </p>
        </div>
    ); 
}; 